import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../lib/apiClient';
import {
  Mail, Search, X, Send, Loader2, ShieldAlert, CheckCircle2, FileWarning, Filter,
  Lock, UserPlus, Globe, ClipboardList, Building2, Palette, ShoppingCart, Bell, Shield, Settings
} from 'lucide-react';

/**
 * EmailLibrarySection — read-only catalog of every email send-point in the
 * platform, plus a hardened "Test Send" probe.
 *
 * Surface contract:
 *   • Mounted inside UltraAdminSettings as one of the section panels.
 *   • Pulls /api/ultra-admin/email-catalog (Ultra Admin only).
 *   • Test-send hits /api/ultra-admin/email-catalog/test-send with the
 *     selected entry id + (admin's email | manually-entered probe).
 *   • Never displays API keys, hashes, or DB rows — the catalog file
 *     itself is the only source.
 */

// Map catalog category icon strings to lucide components.
const ICON_MAP = {
  lock: Lock,
  'user-plus': UserPlus,
  globe: Globe,
  clipboard: ClipboardList,
  building: Building2,
  palette: Palette,
  'shopping-cart': ShoppingCart,
  bell: Bell,
  shield: Shield,
  settings: Settings,
};

// Type chip color/label by entry.type.
const TYPE_STYLE = {
  user_self:    { bg: 'bg-emerald-50', fg: 'text-emerald-700', label_tr: 'Kullanıcı self', label_en: 'User self' },
  user_action:  { bg: 'bg-emerald-50', fg: 'text-emerald-700', label_tr: 'Kullanıcı eylemi', label_en: 'User action' },
  manual:       { bg: 'bg-blue-50',    fg: 'text-blue-700',    label_tr: 'Manuel',          label_en: 'Manual' },
  manual_admin: { bg: 'bg-blue-50',    fg: 'text-blue-700',    label_tr: 'Admin manuel',    label_en: 'Admin manual' },
  bulk_admin:   { bg: 'bg-violet-50',  fg: 'text-violet-700',  label_tr: 'Toplu (Admin)',   label_en: 'Bulk (Admin)' },
  automated:    { bg: 'bg-amber-50',   fg: 'text-amber-700',   label_tr: 'Otomatik',        label_en: 'Automated' },
  diagnostic:   { bg: 'bg-slate-100',  fg: 'text-slate-700',   label_tr: '2FA / Diagnostic',label_en: '2FA / Diagnostic' },
};

// TR-aware normalizer for search.
const norm = (s) => (s ? String(s).toLocaleLowerCase('tr-TR')
  .replace(/[ışçğöü]/g, (c) => ({ 'ı': 'i', 'ş': 's', 'ç': 'c', 'ğ': 'g', 'ö': 'o', 'ü': 'u' })[c] || c) : '');

export default function EmailLibrarySection({ tr }) {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const [search, setSearch]   = useState('');
  const [catFilter, setCat]   = useState('');
  const [typeFilter, setType] = useState('');

  const [testFor, setTestFor] = useState(null);              // entry being tested
  const [testEmail, setTestEmail] = useState('');             // override input
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult]   = useState(null);       // { ok, msg }

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await apiClient.get('/ultra-admin/email-catalog');
        if (alive) setCatalog(r.data);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    if (!catalog) return [];
    const q = norm(search);
    return (catalog.emails || []).filter((e) => {
      if (catFilter && e.category !== catFilter) return false;
      if (typeFilter && e.type   !== typeFilter) return false;
      if (!q) return true;
      const hay = [
        e.id, e.trigger_tr, e.trigger_en, e.recipient_tr, e.recipient_en,
        e.subject_tr, e.subject_en, e.endpoint, e.file,
      ].map(norm).join(' ');
      return hay.includes(q);
    });
  }, [catalog, search, catFilter, typeFilter]);

  // Group filtered entries by category for display.
  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((e) => {
      if (!map.has(e.category)) map.set(e.category, []);
      map.get(e.category).push(e);
    });
    return map;
  }, [filtered]);

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-gray-500 py-12 justify-center">
        <Loader2 className="h-5 w-5 animate-spin" /> {tr ? 'Yükleniyor…' : 'Loading…'}
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
        {tr ? 'Katalog yüklenemedi: ' : 'Failed to load catalog: '}{error}
      </div>
    );
  }

  const t = (en, trTxt) => (tr ? trTxt : en);
  const total   = catalog?.summary?.total || 0;
  const byType  = catalog?.summary?.by_type || {};

  return (
    <div className="space-y-6" data-testid="email-library">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="h-5 w-5 text-purple-700" />
            {t('Email Library', 'E-posta Kütüphanesi')}
          </h2>
          <p className="text-sm text-gray-400 mt-0.5">
            {t('Every email the platform sends — single source of truth.',
               'Platformun gönderdiği tüm e-postalar — tek doğru kaynak.')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 font-bold" data-testid="email-library-total">
            {tr ? 'Toplam' : 'Total'}: {total}
          </span>
        </div>
      </div>

      {/* Search + filters */}
      <div className="flex flex-col md:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('Search trigger, subject, endpoint…', 'Tetikleyici, konu, endpoint ara…')}
            data-testid="email-library-search"
            className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-purple-400 focus:ring-2 focus:ring-purple-100 outline-none"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <select value={catFilter} onChange={(e) => setCat(e.target.value)} data-testid="email-library-filter-cat"
                className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white">
          <option value="">{t('All categories', 'Tüm kategoriler')}</option>
          {(catalog?.categories || []).map((c) => (
            <option key={c.id} value={c.id}>{tr ? c.label_tr : c.label_en} ({catalog?.summary?.by_category?.[c.id] || 0})</option>
          ))}
        </select>
        <select value={typeFilter} onChange={(e) => setType(e.target.value)} data-testid="email-library-filter-type"
                className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white">
          <option value="">{t('All types', 'Tüm tipler')}</option>
          {Object.entries(TYPE_STYLE).map(([key, st]) => (
            <option key={key} value={key}>{tr ? st.label_tr : st.label_en} ({byType[key] || 0})</option>
          ))}
        </select>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="text-center py-16 text-gray-400 text-sm bg-gray-50 rounded-2xl">
          <Filter className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          {t('No matching email entries.', 'Eşleşen e-posta girdisi yok.')}
        </div>
      )}

      {/* Grouped entries */}
      {Array.from(grouped.entries()).map(([catId, entries]) => {
        const cat = (catalog?.categories || []).find((c) => c.id === catId);
        const Icon = ICON_MAP[cat?.icon] || Mail;
        return (
          <div key={catId} className="space-y-2">
            <div className="flex items-center gap-2 px-1">
              <Icon className="h-4 w-4 text-gray-500" />
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">
                {tr ? cat?.label_tr : cat?.label_en} <span className="text-gray-400 font-normal">· {entries.length}</span>
              </h3>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {entries.map((e) => {
                const ts = TYPE_STYLE[e.type] || { bg: 'bg-gray-100', fg: 'text-gray-700', label_tr: e.type, label_en: e.type };
                return (
                  <div key={e.id} data-testid={`email-entry-${e.id}`}
                       className="bg-white border border-gray-200 rounded-2xl p-4 hover:border-purple-200 hover:shadow-sm transition-all">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${ts.bg} ${ts.fg}`}>
                            {tr ? ts.label_tr : ts.label_en}
                          </span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-mono">{e.lang}</span>
                          <span className="text-[10px] text-gray-400 font-mono truncate">{e.id}</span>
                        </div>
                        <p className="font-semibold text-gray-900 text-sm leading-snug">
                          {tr ? e.trigger_tr : e.trigger_en}
                        </p>
                        <div className="mt-2 space-y-1 text-xs text-gray-600">
                          <div><span className="text-gray-400">{tr ? 'Alıcı:' : 'To:'}</span> {tr ? e.recipient_tr : e.recipient_en}</div>
                          <div className="truncate"><span className="text-gray-400">{tr ? 'Konu:' : 'Subject:'}</span> {tr ? e.subject_tr : e.subject_en}</div>
                          <div className="font-mono text-[11px] text-gray-500 truncate"><span className="text-gray-400">↗</span> {e.endpoint}</div>
                          <div className="font-mono text-[11px] text-gray-400 truncate">{e.file}:{e.line}</div>
                        </div>
                      </div>
                      <button
                        onClick={() => { setTestFor(e); setTestEmail(''); setTestResult(null); }}
                        disabled={e.test_safe === false}
                        data-testid={`email-test-${e.id}`}
                        title={e.test_safe === false ? (e.test_note_tr || (tr ? 'Test devre dışı' : 'Test disabled')) : (tr ? 'Test gönder' : 'Send test')}
                        className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                          e.test_safe === false
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
                        }`}
                      >
                        {e.test_safe === false ? <ShieldAlert className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
                        {tr ? 'Test' : 'Test'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Test-send modal */}
      {testFor && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setTestFor(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="email-test-modal">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Send className="h-4 w-4 text-purple-700" />
                <h3 className="font-bold text-gray-900">{tr ? 'Test E-postası Gönder' : 'Send Test Email'}</h3>
              </div>
              <button onClick={() => setTestFor(null)} className="text-gray-400 hover:text-gray-700"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 flex gap-2">
                <FileWarning className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  {tr
                    ? 'Bu probe gerçek müşteriye gitmez. Konu satırı [TEST EMAIL] ile başlar, gövde safe template kullanır. Hiçbir gerçek veri (PDF, magic-link, fiyat) yer almaz.'
                    : 'This probe is never sent to a real customer. Subject is prefixed [TEST EMAIL] and the body uses a safe template — no real PII, magic tokens, or pricing.'}
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-3 text-xs space-y-1">
                <div className="font-mono text-purple-700">{testFor.id}</div>
                <div className="text-gray-600">{tr ? testFor.trigger_tr : testFor.trigger_en}</div>
                <div className="text-gray-400 font-mono truncate">{testFor.endpoint}</div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-gray-600">
                  {tr ? 'Test alıcı (boş bırakırsanız kendi e-postanıza gider)' : 'Test recipient (defaults to your own email)'}
                </label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(ev) => setTestEmail(ev.target.value)}
                  placeholder={tr ? 'kendi-mailim@example.com' : 'your-email@example.com'}
                  data-testid="email-test-recipient"
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:border-purple-400 focus:ring-2 focus:ring-purple-100 outline-none"
                />
              </div>

              {testResult && (
                <div className={`rounded-xl p-3 text-xs flex items-start gap-2 ${
                  testResult.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'
                }`} data-testid="email-test-result">
                  {testResult.ok ? <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" /> : <ShieldAlert className="h-4 w-4 flex-shrink-0 mt-0.5" />}
                  <div className="break-words">{testResult.msg}</div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-2">
              <button onClick={() => setTestFor(null)} className="flex-1 py-2 rounded-xl border border-gray-200 text-sm">{tr ? 'Kapat' : 'Close'}</button>
              <button
                onClick={async () => {
                  setTestSending(true);
                  setTestResult(null);
                  try {
                    const payload = { template_id: testFor.id };
                    if (testEmail.trim()) payload.recipient_override = testEmail.trim();
                    const r = await apiClient.post('/ultra-admin/email-catalog/test-send', payload);
                    setTestResult({ ok: true, msg: tr
                      ? `Test gönderildi → ${r.data.recipient}. (${r.data.subject})`
                      : `Test sent → ${r.data.recipient}. (${r.data.subject})` });
                  } catch (err) {
                    setTestResult({ ok: false, msg: err.response?.data?.detail || err.message });
                  } finally {
                    setTestSending(false);
                  }
                }}
                disabled={testSending}
                data-testid="email-test-confirm"
                className="flex-1 py-2 rounded-xl bg-purple-700 hover:bg-purple-800 text-white text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60"
              >
                {testSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                {tr ? 'Test Gönder' : 'Send Test'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
